import React, { Fragment, useEffect, useState } from "react";
import { Row, Table, Button, Col } from "reactstrap";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';
import JobListGlobalFilter from "./GlobalSearchFilter";

// Column Filter
const Filter = ({
  column
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '')}
        onChange={value => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <React.Fragment>
      <Col sm={4}>
        <input {...props} value={value} onChange={e => setValue(e.target.value)} />
      </Col>
    </React.Fragment>
  );
};

const TableContainer = ({
  columns,
  data,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  isGlobalFilter,
  paginationWrapper,
  SearchPlaceholder,
  pagination,
  buttonClass,
  buttonName,
  isAddButton,
  isCustomPageSize,
  handleUserClick,
  isJobListGlobalFilter,
  // Server-side pagination
  serverSidePagination,
  totalRowCount = 0,
  pageIndex: controlledPageIndex = 0,
  pageSize: controlledPageSize = 10,
  onPaginationChange,
}) => {

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };

  const isServerSide = Boolean(serverSidePagination && onPaginationChange);
  const pageCount = isServerSide && totalRowCount > 0 && controlledPageSize > 0
    ? Math.ceil(totalRowCount / controlledPageSize)
    : undefined;

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      ...(isServerSide ? {
        pagination: {
          pageIndex: controlledPageIndex,
          pageSize: controlledPageSize,
        },
      } : {}),
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: isServerSide ? undefined : getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(isServerSide ? {
      manualPagination: true,
      pageCount,
      onPaginationChange: (updater) => {
        const next = updater({ pageIndex: controlledPageIndex, pageSize: controlledPageSize });
        onPaginationChange(next.pageIndex, next.pageSize);
      },
    } : {}),
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    // setPageSize,
    getState
  } = table;

  // useEffect(() => {
  //   Number(customPageSize) && setPageSize(Number(customPageSize));
  // }, [customPageSize, setPageSize]);

  return (
    <Fragment>

      <Row className="mb-2">
        {isCustomPageSize && (
          <Col sm={2}>
            <select
              className="form-select pageSize mb-2"
              value={table.getState().pagination.pageSize}
              onChange={e => {
                const newSize = Number(e.target.value);
                if (isServerSide) onPaginationChange(0, newSize);
                else table.setPageSize(newSize);
              }}
            >
              {[10, 20, 30, 40, 50].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}

        {isGlobalFilter && <DebouncedInput
          value={globalFilter ?? ''}
          onChange={value => setGlobalFilter(String(value))}
          className="form-control search-box me-2 mb-2 d-inline-block"
          placeholder={SearchPlaceholder}
        />}

        {isJobListGlobalFilter && <JobListGlobalFilter setGlobalFilter={setGlobalFilter} />}

        {isAddButton && <Col sm={6}>
          <div className="text-sm-end">
            <Button type="button" className={buttonClass} onClick={handleUserClick}>
              <i className="mdi mdi-plus me-1"></i> {buttonName}</Button>
          </div>
        </Col>}
      </Row>

      <div className={divClassName ? divClassName : "table-responsive"}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th key={header.id} colSpan={header.colSpan} className={`${header.column.columnDef.enableSorting ? "sorting sorting_desc" : ""}`}>
                      {header.isPlaceholder ? null : (
                        <React.Fragment>
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : '',
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {
                              {
                                asc: '',
                                desc: '',
                              }
                              [header.column.getIsSorted()] ?? null}
                          </div>
                          {header.column.getCanFilter() ? (
                            <div>
                              <Filter column={header.column} table={table} />
                            </div>
                          ) : null}
                        </React.Fragment>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map(row => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>


      {
        isPagination && (
          <Row className="justify-content-md-end justify-content-center align-items-center">
            <Col>
              <div className="dataTables_info">
                {isServerSide && totalRowCount >= 0
                  ? (totalRowCount === 0
                    ? "0 Results"
                    : `Showing ${Math.min(getState().pagination.pageIndex * getState().pagination.pageSize + 1, totalRowCount)} to ${Math.min((getState().pagination.pageIndex + 1) * getState().pagination.pageSize, totalRowCount)} of ${totalRowCount} Results`)
                  : `Showing ${getState().pagination.pageSize} of ${data.length} Results`}
              </div>
            </Col>
            <Col className="col-md-auto">
              <div className={paginationWrapper}>
                <ul className={pagination}>
                  <li className={`paginate_button page-item previous ${!getCanPreviousPage() ? "disabled" : ""}`}>
                    <a href="#" className="page-link" onClick={(e) => {
                      e.preventDefault();
                      if (isServerSide) onPaginationChange(getState().pagination.pageIndex - 1, getState().pagination.pageSize);
                      else previousPage();
                    }}><i className="mdi mdi-chevron-left"></i></a>
                  </li>
                  {/* Limit page numbers to max 20, show ellipsis if needed */}
                  {(() => {
                    const pageOptions = getPageOptions();
                    const pageIndex = getState().pagination.pageIndex;
                    const totalPages = isServerSide ? pageCount : pageOptions.length;
                    const maxPages = 20;
                    let start = 0;
                    let end = totalPages;
                    let items = [];
                    if (totalPages > maxPages) {
                      if (pageIndex < Math.floor(maxPages / 2)) {
                        start = 0;
                        end = maxPages;
                      } else if (pageIndex > totalPages - Math.ceil(maxPages / 2)) {
                        start = totalPages - maxPages;
                        end = totalPages;
                      } else {
                        start = pageIndex - Math.floor(maxPages / 2);
                        end = pageIndex + Math.ceil(maxPages / 2);
                      }
                    }
                    // Always show first page
                    if (start > 0) {
                      items.push(
                        <li key={0} className={`paginate_button page-item ${pageIndex === 0 ? "active" : ""}`}>
                          <a href="#" className="page-link" onClick={(e) => {
                            e.preventDefault();
                            if (isServerSide) onPaginationChange(0, getState().pagination.pageSize);
                            else setPageIndex(0);
                          }}>1</a>
                        </li>
                      );
                      if (start > 1) {
                        items.push(
                          <li key="start-ellipsis" className="paginate_button page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
                    }
                    for (let i = start; i < end; i++) {
                      // Don't repeat first/last page
                      if (i === 0 || i === totalPages - 1) continue;
                      items.push(
                        <li key={i} className={`paginate_button page-item ${pageIndex === i ? "active" : ""}`}>
                          <a href="#" className="page-link" onClick={(e) => {
                            e.preventDefault();
                            if (isServerSide) onPaginationChange(i, getState().pagination.pageSize);
                            else setPageIndex(i);
                          }}>{i + 1}</a>
                        </li>
                      );
                    }
                    // Always show last page
                    if (end < totalPages) {
                      if (end < totalPages - 1) {
                        items.push(
                          <li key="end-ellipsis" className="paginate_button page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
                      items.push(
                        <li key={totalPages - 1} className={`paginate_button page-item ${pageIndex === totalPages - 1 ? "active" : ""}`}>
                          <a href="#" className="page-link" onClick={(e) => {
                            e.preventDefault();
                            if (isServerSide) onPaginationChange(totalPages - 1, getState().pagination.pageSize);
                            else setPageIndex(totalPages - 1);
                          }}>{totalPages}</a>
                        </li>
                      );
                    }
                    return items;
                  })()}
                  <li className={`paginate_button page-item next ${!getCanNextPage() ? "disabled" : ""}`}>
                    <a href="#" className="page-link" onClick={(e) => {
                      e.preventDefault();
                      if (isServerSide) onPaginationChange(getState().pagination.pageIndex + 1, getState().pagination.pageSize);
                      else nextPage();
                    }}><i className="mdi mdi-chevron-right"></i></a>
                  </li>
                </ul>
              </div>
            </Col>
          </Row>
        )
      }
    </Fragment>
  );
};

export default TableContainer;